import { useRef } from "react";
import {
  readLivenessResult,
  readFaceMatchResult,
} from "../utils/participantStats";
import {
  OUTCOME,
  classifyFaceDetect,
  classifySpoof,
  classifyFaceMatch,
} from "../utils/verificationFailure";

const API_BASE = `${process.env.REACT_APP_VIDEOSDK_URL}/ai/v1/face-verification`;

const FAILURE_THRESHOLD = 3;

/**
 * Shared face-verification engine used by both the recorder (owner mode) and the
 * client (delegate mode).
 *
 * - Owner mode (recorder): runs the full sequential pipeline
 *   detect-faces -> detect-spoof -> facematch off the customer's streamed frames,
 *   publishes the verdict channels, and tracks a per-API consecutive-failure
 *   counter. After FAILURE_THRESHOLD failures on a single API it delegates ONLY
 *   that API to the client via the DELEGATE_API channel and pauses the ENTIRE
 *   pipeline until the client reports success (handback).
 *
 * - Delegate mode (client): runs exactly one API on its own local frames until it
 *   succeeds (used only as a fallback when the recorder asks).
 *
 * Two DISTINCT notions of "failure" live here, and they must not be conflated:
 *   - DELEGATION counts ANY non-pass (an api_error OR a real negative verdict).
 *     It is a bandwidth fallback, so even a dropped request should delegate.
 *   - The FRAUD outcome feed (publishOutcome -> VERIFICATION_OUTCOME) is
 *     classified into pass / real_fail / api_error, and ONLY real_fail counts —
 *     so a network error can never push a customer toward SUSPECTED_FRAUD.
 *
 * callbacks.onApiResult({ apiName, body, page }) fires once per ANSWERED call to
 * any of the three endpoints, carrying the response body verbatim. It is the
 * source of the IDENTITY audit log. Because detect-spoof and facematch are the
 * same functions in both modes, a delegated call fires it on the CLIENT — which
 * is why the client forwards it to the recorder over IDENTITY_RESULT instead of
 * writing the log itself. api_error never fires it (no body to record), and
 * neither does the cached detect-faces short-circuit (no call was made).
 */
export default function useFaceVerification({
  mode = "owner",
  captureImage,
  webcamOn,
  token,
  roomId,
  abortSignalRef,
  currentPage,
  referenceImageRef,
  publishers,
  callbacks,
}) {
  // pipeline progress flags
  const spoofCheckDoneRef = useRef(false);
  const faceDetectionDoneRef = useRef(false);
  const faceMatchDoneRef = useRef(false);

  // per-API consecutive-failure counters + active delegation
  const faceFailRef = useRef(0);
  const spoofFailRef = useRef(0);
  const matchFailRef = useRef(0);
  const delegatedApiRef = useRef(null);

  // Last outcome PUBLISHED per API (owner mode). Lets us emit every real_fail
  // but only a single "recovery" pass, instead of a pass on every steady tick.
  const lastPublishedOutcomeRef = useRef({
    faces: null,
    spoof: null,
    match: null,
  });

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // ---- debug logging -----------------------------------------------------
  // Every line is tagged with mode + page so the recorder's and the client's
  // consoles can be read side by side.
  const log = (...args) =>
    console.log(`[FaceVerify:${mode}][${currentPage}]`, ...args);

  const logState = () =>
    log("state:", {
      webcamOn,
      delegatedApi: delegatedApiRef.current,
      roomId,
      fails: {
        faces: faceFailRef.current,
        spoof: spoofFailRef.current,
        match: matchFailRef.current,
      },
      done: {
        faceDetection: faceDetectionDoneRef.current,
        spoof: spoofCheckDoneRef.current,
        faceMatch: faceMatchDoneRef.current,
      },
      hasReferenceImage: !!referenceImageRef?.current,
    });

  // ---- verdict publishing (owner only) ----------------------------------
  // We also notify the recorder's own context directly via callbacks so its UI
  // updates without depending on pubsub echoing self-published messages back.
  const publishAgreed = (isAgreed) => {
    callbacks?.onAgreed?.(isAgreed);
    return (
      publishers?.agreedPublish &&
      publishers.pubsubPublishMethod(
        publishers.agreedPublish,
        "Agreed",
        { persist: true },
        { isAgreed },
      )
    );
  };

  const publishVerified = (isVerified) => {
    callbacks?.onVerified?.(isVerified);
    return (
      publishers?.faceVerifiedPublish &&
      publishers.pubsubPublishMethod(
        publishers.faceVerifiedPublish,
        "Verified",
        { persist: true },
        { isVerified },
      )
    );
  };

  const publishError = (err) => {
    callbacks?.onError?.(err);
    return (
      publishers?.faceDetectErrorPublish &&
      publishers.pubsubPublishMethod(
        publishers.faceDetectErrorPublish,
        "Error",
        { persist: true },
        { err },
      )
    );
  };

  const publishAbort = (isAbort) =>
    publishers?.abortSignalPublish &&
    publishers.pubsubPublishMethod(
      publishers.abortSignalPublish,
      "Abort",
      { persist: true },
      { isAbort },
    );

  // ---- fraud-counter outcome feed (owner only) ---------------------------
  // Publishes a per-API classified verdict for the client's journey-wide
  // failure counters. api_error is NEVER published (must not count as fraud).
  // A pass is published only right after a real_fail (a "recovery"), so a page
  // that keeps verifying doesn't flood the persisted channel with a pass/tick.
  const publishOutcome = (apiName, outcome) => {
    if (outcome === OUTCOME.API_ERROR) return;
    const last = lastPublishedOutcomeRef.current[apiName];
    if (outcome === OUTCOME.PASS && last !== OUTCOME.REAL_FAIL) return;
    lastPublishedOutcomeRef.current[apiName] = outcome;
    callbacks?.onOutcome?.(apiName, outcome);
    publishers?.verificationOutcomePublish &&
      publishers.pubsubPublishMethod(
        publishers.verificationOutcomePublish,
        "Outcome",
        { persist: true },
        { apiName, outcome },
      );
  };

  // ---- raw API calls -----------------------------------------------------
  // Sent with every request so the backend can attribute a check to the journey
  // step it gated, and to WHO ran it: the recorder scoring the customer's
  // streamed frames, or the client running the same API on its own local frames
  // as a delegated fallback. Both sides hit the same endpoints, so without
  // `source` their calls are indistinguishable in the logs.
  const requestMetadata = () => ({
    roomId,
    page: currentPage,
    source: mode === "owner" ? "recorder" : "delegate",
  });

  const verifyNumberOfFaces = async (sendImage) => {
    const signal = abortSignalRef?.current?.signal;

    // Owner only. In delegate mode this short-circuit would make a REPEAT
    // "faces" delegation return PASS with no API call — the client would report
    // a success it never checked, the recorder now turns that into a done-flag,
    // and onApiResult is deliberately skipped here so the audit log would have
    // no row for it either. The client must re-verify on every delegation.
    if (
      mode === "owner" &&
      (currentPage === "PersonalDetails" || currentPage === "PolicyDetails") &&
      faceDetectionDoneRef.current
    ) {
      return { outcome: OUTCOME.PASS, count: 1 };
    }

    let status;
    let body;
    let error;
    try {
      const res = await fetch(`${API_BASE}/detect-faces`, {
        method: "POST",
        headers: {
          authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ img: sendImage, metadata: requestMetadata() }),
        signal,
      });
      status = res.status;
      if (res.ok) body = await res.json();
    } catch (e) {
      error = e;
    }

    const outcome = classifyFaceDetect({ error, status, body });
    if (
      (currentPage === "PersonalDetails" || currentPage === "PolicyDetails") &&
      outcome === OUTCOME.PASS
    ) {
      faceDetectionDoneRef.current = true;
    }
    // Only fired when the endpoint answered. An api_error has no body, so it
    // would log a row asserting a call that produced nothing — and these rows
    // double as the journey's record of how many calls actually happened.
    // Note this sits AFTER the short-circuit above, deliberately: a cached pass
    // never touched the network and must not be counted as a call.
    if (outcome !== OUTCOME.API_ERROR) {
      callbacks?.onApiResult?.({ apiName: "faces", body, page: currentPage });
    }
    return { outcome, count: body?.number_of_faces };
  };

  const detectSpoof = async (sendImage) => {
    const signal = abortSignalRef?.current?.signal;
    let status;
    let body;
    let error;
    try {
      const res = await fetch(`${API_BASE}/detect-spoof`, {
        method: "POST",
        headers: {
          authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ img: sendImage, metadata: requestMetadata() }),
        signal,
      });
      status = res.status;
      if (res.ok) body = await res.json();
    } catch (e) {
      error = e;
    }

    const outcome = classifySpoof({ error, status, body });
    // Only refresh the liveness panel when the endpoint actually answered; a
    // network error / abort leaves the last known score rather than a fake zero.
    if (outcome !== OUTCOME.API_ERROR) {
      callbacks?.onLivenessResult?.(readLivenessResult(body));
      callbacks?.onApiResult?.({ apiName: "spoof", body, page: currentPage });
    }
    return { outcome };
  };

  const faceMatch = async (img1, img2) => {
    const signal = abortSignalRef?.current?.signal;
    let status;
    let body;
    let error;
    try {
      const res = await fetch(`${API_BASE}/verify`, {
        method: "POST",
        headers: {
          authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ img1, img2, metadata: requestMetadata() }),
        signal,
      });
      status = res.status;
      if (res.ok) body = await res.json();
    } catch (e) {
      error = e;
    }

    const outcome = classifyFaceMatch({ error, status, body });
    // Only fired when /verify actually answered, so the panel keeps showing "—"
    // on a dropped request rather than claiming the faces did not match.
    if (outcome !== OUTCOME.API_ERROR) {
      callbacks?.onFaceMatchResult?.(readFaceMatchResult(body));
      callbacks?.onApiResult?.({ apiName: "match", body, page: currentPage });
    }
    return { outcome };
  };

  // ---- delegation bookkeeping (owner) ------------------------------------
  const noteFailure = (ref, apiName) => {
    ref.current += 1;
    callbacks?.onFailure?.(apiName, ref.current);
    if (ref.current >= FAILURE_THRESHOLD && delegatedApiRef.current !== apiName) {
      delegatedApiRef.current = apiName;
      callbacks?.onDelegate?.(apiName, ref.current);
      publishers?.delegateApiPublish &&
        publishers.pubsubPublishMethod(
          publishers.delegateApiPublish,
          "DelegateApi",
          { persist: true },
          { apiName, delegate: true, currentPage },
        );
    }
  };

  // Called on the recorder when the client reports a successful fallback run.
  //
  // The client ran this API against its own LOCAL camera frames, which are
  // strictly better than the degraded stream the recorder is scoring — so its
  // verdict is the more trustworthy one. Mark the check DONE, not just reset the
  // counter: otherwise the recorder immediately re-runs the same check on the
  // same bad stream, fails FAILURE_THRESHOLD times again and re-delegates, and
  // the step never completes.
  //
  // Note which pages each flag actually gates:
  //   faceDetectionDoneRef / faceMatchDoneRef -> read only on PersonalDetails +
  //     PolicyDetails, so setting them on VideoConsent/QnA is inert and the
  //     required continuous 5s monitoring there is preserved.
  //   spoofCheckDoneRef -> read on every page, which matches spec: spoof is
  //     until-success per step and never continuous.
  //
  // `page` is the page the client ran the delegated check on, and it MUST match
  // the page we are on now. This guard is load-bearing, not defensive: because
  // handback sets done-flags — and spoofCheckDoneRef is read on EVERY page —
  // applying an earlier step's success here would skip the liveness check for
  // the whole rest of the journey. DELEGATED_API_SUCCESS crosses the wire, so a
  // success can arrive late or be replayed after the step it belongs to is over.
  // A caller that omits `page` is rejected too: this fails closed by design.
  const handback = (apiName, page) => {
    if (page !== currentPage) {
      log("handback: IGNORED — success belongs to a different page", {
        apiName,
        reportedFor: page,
        currentPage,
      });
      return;
    }

    if (apiName === "faces") {
      faceFailRef.current = 0;
      faceDetectionDoneRef.current = true;
    } else if (apiName === "spoof") {
      spoofFailRef.current = 0;
      spoofCheckDoneRef.current = true;
    } else if (apiName === "match") {
      matchFailRef.current = 0;
      faceMatchDoneRef.current = true;
    }

    log("handback: client verified delegated API — marking check done", {
      apiName,
      done: {
        faceDetection: faceDetectionDoneRef.current,
        spoof: spoofCheckDoneRef.current,
        faceMatch: faceMatchDoneRef.current,
      },
    });

    if (delegatedApiRef.current === apiName) {
      delegatedApiRef.current = null;
      publishers?.delegateApiPublish &&
        publishers.pubsubPublishMethod(
          publishers.delegateApiPublish,
          "DelegateApi",
          { persist: true },
          { apiName, delegate: false, currentPage },
        );
    }
  };

  // Clear all delegation/counters (call on abort or page reset).
  const resetDelegation = () => {
    faceFailRef.current = 0;
    spoofFailRef.current = 0;
    matchFailRef.current = 0;
    delegatedApiRef.current = null;
  };

  // ---- owner pipeline (recorder) -----------------------------------------
  async function runOwnerPipeline() {
    log("runOwnerPipeline: start");
    logState();

    if (!webcamOn) {
      log("runOwnerPipeline: abort -> webcam is off (NO_CAMERA_PERMISSIONS)");
      publishAgreed(false);
      publishAbort(false);
      publishError("NO_CAMERA_PERMISSIONS");
      return;
    }

    // While ANY api is delegated the recorder runs nothing at all — not just the
    // delegated check. The delegation exists because this side's stream is too
    // degraded to score, so spending bandwidth on the other two checks would
    // only produce more failures on the same bad frames. Resumes on handback.
    if (delegatedApiRef.current) {
      log("pipeline: skipped -> delegated to client, waiting for handback", {
        delegatedApi: delegatedApiRef.current,
      });
      return;
    }

    // 1) FACE DETECTION
    if (delegatedApiRef.current === "faces" || delegatedApiRef.current === "match" || delegatedApiRef.current === "spoof") return; // waiting for client

    let base64Data = await captureImage(500, 400);
    let sendImage = "data:image/jpeg;base64," + base64Data;
    log("step 1 faces: captured frame", { bytes: base64Data?.length ?? 0 });
    const facesResult = await verifyNumberOfFaces(sendImage);
    publishOutcome("faces", facesResult.outcome);
    log("step 1 faces: result", facesResult);

    if (facesResult.outcome === OUTCOME.PASS) {
      faceFailRef.current = 0;
      log("step 1 faces: OK (counter reset)");
    } else {
      const err =
        facesResult.count > 1 ? "MULTIPLE_FACES_DETECTED" : "NO_FACE_DETECTED";
      log("step 1 faces: FAIL", {
        outcome: facesResult.outcome,
        count: facesResult.count,
        err,
        fails: faceFailRef.current + 1,
      });
      noteFailure(faceFailRef, "faces");
      publishAgreed(false);
      publishAbort(false);
      publishError(err);
      return;
    }

    // 2) SPOOF / LIVENESS
    if (!spoofCheckDoneRef.current) {
      let spoofOutcome = OUTCOME.REAL_FAIL;
      let spoofAttempt = 0;
      while (spoofOutcome !== OUTCOME.PASS) {
        if (delegatedApiRef.current === "spoof") {
          log("step 2 spoof: delegated to client, exiting pipeline");
          return; // waiting for client
        }

        spoofAttempt += 1;
        base64Data = await captureImage(500, 400);
        sendImage = "data:image/jpeg;base64," + base64Data;
        log("step 2 spoof: captured frame", {
          attempt: spoofAttempt,
          bytes: base64Data?.length ?? 0,
        });
        spoofOutcome = (await detectSpoof(sendImage)).outcome;
        publishOutcome("spoof", spoofOutcome);
        log("step 2 spoof: result", {
          attempt: spoofAttempt,
          outcome: spoofOutcome,
        });

        if (spoofOutcome !== OUTCOME.PASS) {
          log("step 2 spoof: FAIL", { fails: spoofFailRef.current + 1 });
          noteFailure(spoofFailRef, "spoof");
          publishAgreed(false);
          publishError("SPOOF_DETECTED");
          publishAbort(false);
          if (delegatedApiRef.current === "spoof") {
            log("step 2 spoof: just delegated to client, exiting pipeline");
            return; // just delegated
          }
          log("step 2 spoof: retrying in 3000ms");
          await delay(3000);
        } else {
          spoofFailRef.current = 0;
          log("step 2 spoof: OK (counter reset)");
        }
      }
      spoofCheckDoneRef.current = true;
      log("step 2 spoof: done for this page");
    } else {
      log("step 2 spoof: skipped -> already completed");
    }

    // 3) REFERENCE CAPTURE (PersonalDetails) or FACE MATCH (later pages)
    if (currentPage === "PersonalDetails") {
      if (referenceImageRef && referenceImageRef.current == null) {
        referenceImageRef.current = sendImage;
        log("step 3 reference: stored reference image", {
          bytes: sendImage?.length ?? 0,
        });
      } else {
        log("step 3 reference: reference image already present");
      }
      log("step 3 reference: publishing agreed=true, verified=true");
      publishAgreed(true);
      publishVerified(true);
    } else {
      // Already matched on PolicyDetails — skip the API call but still publish
      // the verdict. This branch is also how a handback-set faceMatchDoneRef
      // reaches the UI: the flag can now be set without this recorder ever
      // having published a positive verdict itself, and without this publish the
      // Next button would stay disabled forever. Reaching step 3 means faces and
      // spoof already passed, so a positive verdict here is sound.
      if (currentPage === "PolicyDetails" && faceMatchDoneRef.current) {
        log("step 3 match: skipped -> already matched, republishing verdict");
        publishAgreed(true);
        publishVerified(true);
        return;
      }

      if (delegatedApiRef.current === "match") {
        log("step 3 match:  delegated to client, exiting pipeline");
        return; // waiting for client
      }

      log("step 3 match: calling faceMatch", {
        hasReferenceImage: !!referenceImageRef?.current,
      });
      const matchResult = await faceMatch(sendImage, referenceImageRef?.current);
      publishOutcome("match", matchResult.outcome);
      log("step 3 match: result", matchResult);

      if (matchResult.outcome === OUTCOME.PASS) {
        matchFailRef.current = 0;
        log("step 3 match: OK (counter reset)");
        publishAgreed(true);
        publishVerified(true);
        if (currentPage === "PolicyDetails") faceMatchDoneRef.current = true;
      } else {
        log("step 3 match: FAIL", {
          outcome: matchResult.outcome,
          fails: matchFailRef.current + 1,
        });
        noteFailure(matchFailRef, "match");
        publishAgreed(false);
        publishAbort(false);
        publishError("FACE_MATCH_UNSUCESSFUL");
      }
    }

    log("runOwnerPipeline: end");
  }

  // ---- single-API delegated runner (client fallback) ---------------------
  // Returns { success, outcome } for the requested API.
  async function runDelegatedApi(apiName) {
    log("runDelegatedApi: start", { apiName });

    if (!webcamOn) {
      log("runDelegatedApi: abort -> webcam is off", { apiName });
      return { success: false, outcome: OUTCOME.API_ERROR };
    }

    const base64Data = await captureImage(500, 400);
    const sendImage = "data:image/jpeg;base64," + base64Data;
    log("runDelegatedApi: captured frame", {
      apiName,
      bytes: base64Data?.length ?? 0,
    });

    let outcome = OUTCOME.API_ERROR;
    if (apiName === "faces") {
      ({ outcome } = await verifyNumberOfFaces(sendImage));
    } else if (apiName === "spoof") {
      ({ outcome } = await detectSpoof(sendImage));
    } else if (apiName === "match") {
      ({ outcome } = await faceMatch(sendImage, referenceImageRef?.current));
    } else {
      log("runDelegatedApi: unknown apiName", { apiName });
    }

    const success = outcome === OUTCOME.PASS;
    log("runDelegatedApi: result", { apiName, outcome, success });
    return { success, outcome };
  }

  return {
    runOwnerPipeline,
    runDelegatedApi,
    handback,
    resetDelegation,
    delegatedApiRef,
    faceFailRef,
    spoofFailRef,
    matchFailRef,
    spoofCheckDoneRef,
    faceDetectionDoneRef,
    faceMatchDoneRef,
  };
}
